import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { ROUTES } from '@constants/routes';
import type { CheckoutStep } from '@t/checkout.types';
import type {
  UseCheckoutFlowInput,
  UseCheckoutFlowReturn,
} from '@components/Checkout/hooks/useCheckoutFlow/useCheckoutFlow.types';
import { getCheckoutSteps } from '@components/Checkout/hooks/useCheckoutSteps';
import { useCustomerMetadata } from '@components/Checkout/hooks/useCustomerMetadata';
import { useHydrateCustomerInfo } from '@components/Checkout/hooks/useHydrateCustomerInfo';
import { useHydratePhoneInfo } from '@components/Checkout/hooks/useHydratePhoneInfo';
import { useInstallationDetailsLifecycle } from '@components/Checkout/hooks/useInstallationDetailsLifecycle';
import { usePlaceOrder } from '@components/Checkout/hooks/usePlaceOrder';
import { useReconcileCheckoutSteps } from '@components/Checkout/hooks/useReconcileCheckoutSteps';
import { useSaveCustomerInfo } from '@components/Checkout/hooks/useSaveCustomerInfo/useSaveCustomerInfo';
import { useSaveInstallationInfo } from '@components/Checkout/hooks/useSaveInstallationInfo/useSaveInstallationInfo';
import { useSavePhoneInfo } from '@components/Checkout/hooks/useSavePhoneInfo';
import { useScrollToActiveStep } from '@components/Checkout/hooks/useScrollToActiveStep';
import { useSearchBookingSlots } from '@hooks/useAppointmentSlots';
import { useAnalytics } from '@hooks/useAnalytics/useAnalytics';
import { useConsent } from '@hooks/useConsent';
import { useReleaseOnCartEmpty } from '@hooks/useReleaseOnCartEmpty';
import { useSessionNavigation } from '@hooks/useSessionNavigation';
import { useShoppingCart } from '@hooks/useShoppingCart/useShoppingCart';
import { useCheckoutStore } from '@store/checkoutStore';
import { selectQualifiedAddressId, useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import { useSessionContext } from '@contexts/SessionContext';
import { mapCartInstallationToFormValues } from '@utils/installationUtils/installationUtils';

const TIMEZONE = 'America/Vancouver';

const toIsoDate = (d: Date): string => d.toISOString().slice(0, 10);

export const useCheckoutFlow = ({ resolvedAddressId }: UseCheckoutFlowInput): UseCheckoutFlowReturn => {
  const navigate = useNavigate();
  const { sessionId } = useSessionContext();

  const state = useCheckoutStore(
    useShallow((s) => ({
      stepStatuses: s.stepStatuses,
      activeStep: s.activeStep,
      previousActiveStep: s.previousActiveStep,
      hasPhonePlan: s.hasPhonePlan,
      termsAccepted: s.termsAccepted,
      customerInfo: s.customerInfo,
      installationInfo: s.installationInfo,
      phoneInfo: s.phoneInfo,
    }))
  );

  const actions = useCheckoutStore(
    useShallow((s) => ({
      editStep: s.editStep,
      setTermsAccepted: s.setTermsAccepted,
      reset: s.reset,
      resetForBackToCart: s.resetForBackToCart,
    }))
  );

  const setQualificationFlow = useQualificationFlowStore((s) => s.setQualificationFlow);
  const amsQualifiedAddressId = useQualificationFlowStore(selectQualifiedAddressId);
  const analytics = useAnalytics();

  const [triggerInstallDialog, setTriggerInstallDialog] = useState(false);
  const stepRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const { cart, removePlanAsync, removeAllItems, isPending: isCartMutating } = useShoppingCart(sessionId ?? '');

  const reconciled = useReconcileCheckoutSteps({
    cart,
    customerInfo: state.customerInfo,
    phoneInfo: state.phoneInfo,
    installationInfo: state.installationInfo,
    storeStepStatuses: state.stepStatuses,
    storeActiveStep: state.activeStep,
    storePreviousActiveStep: state.previousActiveStep,
  });

  useHydrateCustomerInfo(sessionId, state.customerInfo);
  useHydratePhoneInfo(sessionId, reconciled.hasPhonePlan, state.phoneInfo);

  const { metadata, isMetadataLoading } = useCustomerMetadata();

  const cartSavedValues = mapCartInstallationToFormValues(cart?.installationDetails ?? null);

  const {
    installationDetailsResult,
    enableFetch: enableInstallationFetch,
    enterInstallationEdit,
  } = useInstallationDetailsLifecycle({
    sessionId,
    cartId: cart?.shoppingCartId,
    reconciledStepStatuses: reconciled.stepStatuses,
    cartHasInstallationDetails: cart?.installationDetails != null,
    amsQualifiedAddressId,
  });
  const { serviceTypeResult, technicianOptions, pickupAddress } = installationDetailsResult;

  const { searchSlots, isSearching: isSlotsLoading, slotsData } = useSearchBookingSlots(sessionId ?? '');

  const { save: saveCustomer, isSaving: isSavingCustomer } = useSaveCustomerInfo({
    sessionId,
    resolvedAddressId,
    savedCustomerInfo: state.customerInfo,
    onSuccess: (values) => {
      enableInstallationFetch();
      setTriggerInstallDialog(true);
      if (sessionId && cart) {
        analytics.fire('trackCustomerInformationSaved', {
          sessionId,
          cart,
          qualification: { qualifiedAddressId: amsQualifiedAddressId, resolvedAddressId },
          customerInfo: values,
        });
      }
    },
  });

  const { save: saveInstallation, isSaving: isSavingInstallation } = useSaveInstallationInfo({
    sessionId,
    slotsData,
    pickupAddress,
  });

  const { save: savePhone, isSaving: isSavingPhone } = useSavePhoneInfo(sessionId);

  const { submitConsent } = useConsent(sessionId ?? '');

  const { placeOrder, isSubmitting, isNavigating, orderError } = usePlaceOrder({ sessionId, cart });

  const { goBackToStep, isPending: isSessionNavigating } = useSessionNavigation(sessionId ?? '');
  const { releaseIfCartEmpty } = useReleaseOnCartEmpty();

  const removeItem = async (offeringId: string): Promise<void> => {
    await removePlanAsync(offeringId);
    releaseIfCartEmpty();
  };

  const clearCart = async (): Promise<void> => {
    await removeAllItems();
    await goBackToStep('SELECT');
    setQualificationFlow((prev) => ({ ...prev, resumeRoute: null }));
    actions.reset();
    navigate(ROUTES.RESIDENTIAL);
  };

  const backToCart = async (): Promise<void> => {
    await goBackToStep('SELECT');
    setQualificationFlow((prev) => ({ ...prev, resumeRoute: null }));
    actions.resetForBackToCart();
    navigate(ROUTES.RESIDENTIAL);
  };

  useScrollToActiveStep(reconciled.activeStep, stepRefs);

  const steps = getCheckoutSteps(reconciled.hasPhonePlan, reconciled.hasInstallationRequired);
  const isEditingPreviousStep = state.previousActiveStep !== null && reconciled.activeStep !== state.previousActiveStep;

  const handleDateRangeChange = (range: { startDate: Date; endDate: Date }): void => {
    if (!sessionId || !serviceTypeResult) return;
    searchSlots({
      from: toIsoDate(range.startDate),
      to: toIsoDate(range.endDate),
      timezone: TIMEZONE,
      serviceTypeResult,
    });
  };

  const handleTermsChange = (accepted: boolean): void => {
    actions.setTermsAccepted(accepted);
    if (accepted) submitConsent({ termsVersion: '1.0', accepted: true });
  };

  const onEditStep = (step: CheckoutStep): void => {
    if (step === 'installation') {
      enterInstallationEdit();
      return;
    }
    actions.editStep(step);
  };

  return {
    steps,
    activeStep: reconciled.activeStep,
    stepStatuses: reconciled.stepStatuses,
    isEditingPreviousStep,
    stepRefs,
    onEditStep,
    customerStepProps: {
      metadata,
      isMetadataLoading,
      savedValues: state.customerInfo,
      isSaving: isSavingCustomer,
      onSave: saveCustomer,
    },
    installationStepProps: {
      sessionId,
      technicianOptions,
      pickupAddress: pickupAddress ?? undefined,
      workTime: slotsData?.workTime ?? [],
      isSlotsLoading,
      savedValues: cartSavedValues,
      isSaving: isSavingInstallation,
      onSave: saveInstallation,
      onDateRangeChange: handleDateRangeChange,
      showInitialInfoDialog: triggerInstallDialog,
      onInfoDialogShown: () => setTriggerInstallDialog(false),
    },
    phoneStepProps: {
      sessionId,
      directoryListings: metadata?.directoryListings ?? [],
      phoneProviders: metadata?.phoneProviders ?? [],
      savedValues: state.phoneInfo,
      isSaving: isSavingPhone,
      onSave: savePhone,
    },
    reviewStepProps: {
      cart: cart ?? null,
      termsAccepted: state.termsAccepted,
      isCartMutating,
      onTermsChange: handleTermsChange,
      onRemoveItem: removeItem,
      onRemoveAllItems: clearCart,
    },
    placeOrder: {
      activeStep: reconciled.activeStep,
      termsAccepted: state.termsAccepted,
      isEditingPreviousStep,
      isSubmitting,
      isNavigating: isNavigating || isSessionNavigating,
      orderError,
      onPlaceOrder: placeOrder,
    },
    header: {
      onBackToCart: backToCart,
    },
    summary: {
      cart: cart ?? null,
    },
    actions: {
      clearCart,
    },
  };
};
